import { useEffect, useRef } from 'react';

/**
 * assistantContext — a tiny global registry that lets the current page
 * publish the data it's showing so the assistant can "lock on" to it.
 *
 * Design: a module-level singleton + CustomEvent notifications (same
 * `banyak:` pattern the rest of the shell uses). Pages register via the
 * useAssistantContext hook; the assistant reads getAssistantContext()
 * when composing a message and listens for 'banyak:assistant_context'
 * to update its lock chip.
 *
 * Contract of a registered context:
 *   {
 *     type,        // 'candidate_list' | 'search_list' | 'outreach_list' | ...
 *     label,       // human label for the chip, e.g. 'Candidates'
 *     rows,        // the visible/filtered rows (array of plain objects)
 *     columns,     // string[] of the columns that matter for analysis
 *     totalCount,  // size of the full dataset (enables the "widen" toggle)
 *     meta,        // { filters, sort, ... } — how the visible set was derived
 *   }
 *
 * Only ONE context is active at a time (the page currently mounted). When
 * a page unmounts it clears its own context (guarded so a fast route
 * change where the new page registers first doesn't get clobbered).
 */

let _current = null;
let _seq = 0;
let _suppressed = false;

// Lock-on suppression: when the user "unlocks", the page context is still
// registered (so re-locking is instant) but getAssistantContext() returns
// null so the launcher won't send it. Cleared automatically when a new
// context registers (a new page re-arms the lock).
export function setContextSuppressed(v) { _suppressed = !!v; }
export function isContextSuppressed() { return _suppressed; }

export function setAssistantContext(ctx) {
  _current = ctx ? { ...ctx, _id: ++_seq } : null;
  _suppressed = false; // a new page re-arms the lock
  try {
    window.dispatchEvent(new CustomEvent('banyak:assistant_context', { detail: { present: !!_current, type: _current?.type, label: _current?.label, count: _current?.rows?.length ?? 0, totalCount: _current?.totalCount ?? null } }));
  } catch {}
}

export function getAssistantContext() {
  return _suppressed ? null : _current;
}

// ───────────────────────────────────────────────────────────────────────────
// Spatial selection ("point-and-click context"). A parallel, more precise
// context that sits ALONGSIDE the full-page context — so the assistant can
// reason over the whole page, the selection, or compare both.
//
// Resolution strategy (bulletproof + long-term):
//   1. STRUCTURED (Option B): selected DOM nodes carry data-context-type /
//      data-context-id. We resolve those ids against the current page
//      context's rows to recover full structured records → analyzable AND
//      actionable (bulk tools work).
//   2. TEXT FALLBACK (Option A): for anything not tagged, we capture the
//      element's visible innerText so the assistant can still read it.
// ───────────────────────────────────────────────────────────────────────────
let _selection = null;

export function getAssistantSelection() {
  return _suppressed ? null : _selection;
}

export function clearAssistantSelection() {
  _selection = null;
  _emitSelection();
}

function _emitSelection() {
  try {
    window.dispatchEvent(new CustomEvent('banyak:assistant_selection', {
      detail: _selection
        ? { present: true, label: _selection.label, count: _selection.count, kind: _selection.kind }
        : { present: false },
    }));
  } catch {}
}

export function getSelectionChipInfo() {
  if (!_selection) return null;
  return { present: true, label: _selection.label, count: _selection.count, kind: _selection.kind };
}

/**
 * Resolve a set of DOM elements into a selection context. Called by the
 * selection overlay with the elements the user clicked or lassoed.
 */
export function setAssistantSelectionFromElements(elements) {
  const els = Array.from(elements || []);
  if (els.length === 0) { clearAssistantSelection(); return; }

  // 1. Structured: collect data-context-id per type from the elements and
  //    their descendants (a dragged rectangle may enclose a container whose
  //    tagged rows are children).
  const tagged = [];
  const seen = new Set();
  for (const el of els) {
    const nodes = [el, ...el.querySelectorAll('[data-context-id]')];
    for (const n of nodes) {
      const id = n.getAttribute?.('data-context-id');
      const type = n.getAttribute?.('data-context-type') || 'item';
      if (id != null && !seen.has(`${type}:${id}`)) {
        seen.add(`${type}:${id}`);
        tagged.push({ type, id: /^\d+$/.test(id) ? Number(id) : id });
      }
    }
  }

  if (tagged.length > 0) {
    // Recover full records from the current page context by id.
    const type = tagged[0].type;
    const idKey = `${type === 'candidate' ? 'candidate_id' : `${type}_id`}`;
    const wantIds = new Set(tagged.map(t => String(t.id)));
    const pool = _current?.rows || [];
    const matched = pool.filter(r => wantIds.has(String(r[idKey] ?? r.id ?? r.candidate_id)));

    _selection = {
      kind: 'structured',
      type,
      label: _current?.label ? `${_current.label} (selected)` : 'Selection',
      count: matched.length || tagged.length,
      // Prefer full records; fall back to bare ids if the page context
      // didn't have them (e.g. selected on a different surface).
      rows: matched.length ? matched : tagged.map(t => ({ [idKey]: t.id })),
      columns: _current?.columns || null,
      ids: tagged.map(t => t.id),
    };
    _emitSelection();
    return;
  }

  // 2. Text fallback: capture visible text from the selected elements.
  const texts = els
    .map(el => (el.innerText || el.textContent || '').trim().replace(/\s+/g, ' '))
    .filter(Boolean)
    .slice(0, 40);
  _selection = {
    kind: 'text',
    type: 'region',
    label: 'Selection',
    count: texts.length,
    text: texts.join('\n---\n').slice(0, 4000),
  };
  _emitSelection();
}

// Chip info for the current context, regardless of suppression (the chip
// should still render when unlocked, just in its unlocked state). Returns
// null when no page has registered. Lets a late-mounting listener (the
// sidebar) read state that was published before it existed.
export function getContextChipInfo() {
  if (!_current) return null;
  return {
    present: true,
    type: _current.type,
    label: _current.label,
    count: _current.rows?.length ?? 0,
    totalCount: _current.totalCount ?? null,
  };
}

export function clearAssistantContext(id) {
  // Only clear if the id matches the currently-registered context, so a
  // page that already registered on route change isn't wiped by the
  // previous page's cleanup.
  if (_current && (id == null || _current._id === id)) {
    setAssistantContext(null);
  }
}

/**
 * Build a bounded snapshot suitable for sending to the model. Small sets
 * send all rows; large sets send a sample + light aggregates so we don't
 * blow the token budget.
 */
export function buildSnapshot(ctx, { maxRows = 40 } = {}) {
  if (!ctx || !Array.isArray(ctx.rows)) return null;
  const rows = ctx.rows;
  const columns = ctx.columns && ctx.columns.length ? ctx.columns : inferColumns(rows);

  const project = (r) => {
    const o = {};
    for (const c of columns) if (r[c] !== undefined) o[c] = r[c];
    return o;
  };

  const truncated = rows.length > maxRows;
  const sample = (truncated ? rows.slice(0, maxRows) : rows).map(project);

  return {
    type: ctx.type,
    label: ctx.label,
    columns,
    visible_count: rows.length,
    total_count: ctx.totalCount ?? rows.length,
    truncated,
    returned_rows: sample.length,
    meta: ctx.meta || null,
    rows: sample,
  };
}

function inferColumns(rows) {
  if (!rows.length) return [];
  const first = rows[0] || {};
  // Prefer human-meaningful primitive fields; skip obvious internals.
  return Object.keys(first)
    .filter(k => !/^_/.test(k) && k !== 'id')
    .filter(k => {
      const v = first[k];
      return v == null || ['string', 'number', 'boolean'].includes(typeof v);
    })
    .slice(0, 12);
}

/**
 * Bounded snapshot of the current selection for the model. Structured
 * selections send their records (like a page snapshot); text selections
 * send the captured text.
 */
export function buildSelectionSnapshot(sel, { maxRows = 60 } = {}) {
  if (!sel) return null;
  if (sel.kind === 'text') {
    return { kind: 'text', label: sel.label, count: sel.count, text: sel.text };
  }
  const rows = Array.isArray(sel.rows) ? sel.rows : [];
  const columns = sel.columns && sel.columns.length ? sel.columns : inferColumns(rows);
  const project = (r) => {
    const o = {};
    for (const c of columns) if (r[c] !== undefined) o[c] = r[c];
    return o;
  };
  const truncated = rows.length > maxRows;
  const sample = (truncated ? rows.slice(0, maxRows) : rows).map(project);
  return {
    kind: 'structured',
    type: sel.type,
    label: sel.label,
    columns,
    count: rows.length,
    truncated,
    returned_rows: sample.length,
    ids: sel.ids || null,
    rows: sample,
  };
}

/**
 * Hook — a page calls this with its current visible data. Re-registers on
 * every change; clears on unmount.
 */
export function useAssistantContext(ctx, enabled = true) {
  const idRef = useRef(null);
  // Serialize the parts that matter so we don't re-register on every render.
  const sig = enabled
    ? `${ctx?.type}|${ctx?.rows?.length}|${ctx?.totalCount}|${ctx?.columns?.join(',')}|${JSON.stringify(ctx?.meta || {})}`
    : 'disabled';

  useEffect(() => {
    if (!enabled || !ctx) return;
    setAssistantContext(ctx);
    idRef.current = _current?._id;
    return () => clearAssistantContext(idRef.current);
  }, [sig, enabled]);
}
